import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AutofillApply, EventIdentity, ShiftPlanning } from '@reditelji/shared';
import { inOrder, latestVersion, mergeSaved } from './cache';
import { api, ApiRequestError } from './client';
import { useSession } from './session';
import type {
  ChangelogPayload,
  CheckinRow,
  EventInfo,
  ProblemRow,
  RaceRoute,
  Shift,
  TimelinePayload,
} from './types';

export function useEvent() {
  return useQuery({ queryKey: ['event'], queryFn: () => api<EventInfo>('/event') });
}

export function useTimeline(eventDayId: string | null) {
  return useQuery({
    queryKey: ['timeline', eventDayId],
    queryFn: () => api<TimelinePayload>(`/timeline?eventDayId=${eventDayId!}`),
    enabled: eventDayId !== null,
    placeholderData: (prev) => prev,
  });
}

export function useRaceRoutes() {
  return useQuery({ queryKey: ['routes'], queryFn: () => api<RaceRoute[]>('/routes') });
}

export interface RouteImportInput {
  name: string;
  color: string;
  sortIndex?: number;
  eventDayIds?: string[];
  sourceName?: string;
  /** Raw GPX document text. */
  gpx: string;
}

/** Import result, plus what the file carried but the app does not use. */
export type RouteImportResult = RaceRoute & { waypointCount: number };

/** What a course's row in Trase can change. */
export type RoutePatch = Partial<Omit<RouteImportInput, 'gpx'> & { weight: number }>;

/** GPX import + Trase panel management (SPEC §1.2). */
export function useRouteMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['routes'] });
    void qc.invalidateQueries({ queryKey: ['changelog'] });
  };
  const common = { onError: mutationErrorHandler, onSettled: invalidate };

  const importGpx = useMutation({
    mutationFn: (input: RouteImportInput) =>
      api<RouteImportResult>('/routes/import', { method: 'POST', body: JSON.stringify(input) }),
    ...common,
  });
  /*
   * A rename closes its input on blur and shows the list's name again, so the
   * typed one goes into the list at once — not a round trip later, looking
   * undone meanwhile. A failed write refetches the stored name back. A second
   * rename started before the first lands waits for it and carries its version.
   */
  const ordered = inOrder(qc, 'routes', ['routes'], invalidate);
  const update = useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & RoutePatch) =>
      api<RaceRoute>(`/routes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ ...patch, updatedAt: latestVersion(qc, ['routes'], id) }),
      }),
    ...ordered,
    onMutate: async ({ id, ...patch }) => {
      await ordered.onMutate();
      qc.setQueryData<RaceRoute[]>(['routes'], (rows) =>
        rows?.map((r) => (r.id === id ? { ...r, ...patch } : r)),
      );
    },
    onSuccess: (saved, { id, ...patch }) =>
      mergeSaved<RaceRoute>(qc, ['routes'], id, saved, Object.keys(patch)),
    onError: mutationErrorHandler,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api<void>(`/routes/${id}`, { method: 'DELETE' }),
    ...common,
  });
  return { importGpx, update, remove };
}

/** Asks nothing for a login without `audit:read`, which the route refuses (SPEC §1.1L). */
export function useChangelog(entity?: string, entityId?: string) {
  const { can } = useSession();
  return useQuery({
    queryKey: ['changelog', entity ?? 'all', entityId ?? 'all'],
    enabled: can('audit:read'),
    queryFn: () =>
      api<ChangelogPayload>(
        `/changelog?limit=80${entity ? `&entity=${entity}` : ''}${entityId ? `&entityId=${entityId}` : ''}`,
      ),
  });
}

function mutationErrorHandler(err: Error) {
  notifications.show({
    color: 'red',
    title: 'Napaka',
    message:
      err instanceof ApiRequestError && err.status === 409
        ? 'Podatek je medtem spremenil nekdo drug. Pogled je osvežen — poskusi znova.'
        : err.message,
  });
}

/** Invalidate everything the timeline screen shows. */
export function useInvalidateTimeline() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['timeline'] });
    // The board reads the other days' shifts too — the hover card's cross-day
    // totals — and those come from ['shifts', 'all'], not from the day payload.
    void qc.invalidateQueries({ queryKey: ['shifts'] });
    void qc.invalidateQueries({ queryKey: ['changelog'] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
  };
}

export interface ShiftCreateInput {
  postId: string;
  eventDayId: string;
  fromMin: number;
  toMin: number;
  volunteerId?: string | null;
  state?: string;
  note?: string;
  meetMin?: number | null;
  meetPlace?: string;
  silent?: boolean;
}

export function useShiftMutations() {
  const invalidate = useInvalidateTimeline();
  const common = { onError: mutationErrorHandler, onSettled: invalidate };

  const create = useMutation({
    mutationFn: (input: ShiftCreateInput) =>
      api<Shift>('/shifts', { method: 'POST', body: JSON.stringify(input) }),
    ...common,
  });
  const update = useMutation({
    mutationFn: ({ id, ...patch }: { id: string; updatedAt: string } & Record<string, unknown>) =>
      api<Shift>(`/shifts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    ...common,
  });
  const remove = useMutation({
    mutationFn: ({ id, silent }: { id: string; silent?: boolean }) =>
      api<void>(`/shifts/${id}${silent ? '?silent=true' : ''}`, { method: 'DELETE' }),
    ...common,
  });
  return { create, update, remove };
}

/** Apply an accepted autofill plan in one request — the server does it atomically. */
export function useAutofillApply() {
  const invalidate = useInvalidateTimeline();
  return useMutation({
    mutationFn: (input: AutofillApply) =>
      api<{ shifts: Shift[]; byVolunteer: { name: string; shifts: number; minutes: number }[] }>(
        '/timeline/autofill/apply',
        { method: 'POST', body: JSON.stringify(input) },
      ),
    onError: mutationErrorHandler,
    onSettled: invalidate,
  });
}

export function useEventMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EventIdentity) =>
      api<EventInfo>('/event', { method: 'PATCH', body: JSON.stringify(input) }),
    onError: mutationErrorHandler,
    onSettled: () => qc.invalidateQueries({ queryKey: ['event'] }),
  });
}

export function useShiftPlanningMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ShiftPlanning) =>
      api<ShiftPlanning>('/event/shift-planning', {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onError: mutationErrorHandler,
    onSettled: () => qc.invalidateQueries({ queryKey: ['event'] }),
  });
}

/** A coordinator's answer for one of a person's shifts, or taking it back (SPEC §1.1F). */
export function useShiftAnswerMutation() {
  const invalidate = useInvalidateTimeline();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      volunteerId,
      shiftId,
      status,
      comment,
    }: {
      volunteerId: string;
      shiftId: string;
      status: 'ok' | 'cant' | 'pending';
      comment?: string;
    }) =>
      api<unknown>(`/volunteers/${volunteerId}/shifts/${shiftId}/answer`, {
        method: 'POST',
        body: JSON.stringify({ status, comment: comment ?? '' }),
      }),
    onError: mutationErrorHandler,
    onSettled: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ['volunteers'] });
    },
  });
}

/** Live mode: org check-in/out + undo on a shift. */
export function useCheckinMutations() {
  const invalidate = useInvalidateTimeline();
  const common = { onError: mutationErrorHandler, onSettled: invalidate };
  const set = useMutation({
    mutationFn: ({ shiftId, kind }: { shiftId: string; kind: 'in' | 'out' }) =>
      api<CheckinRow>(`/shifts/${shiftId}/checkin`, {
        method: 'POST',
        body: JSON.stringify({ kind }),
      }),
    ...common,
  });
  const clear = useMutation({
    mutationFn: ({ shiftId, kind }: { shiftId: string; kind: 'in' | 'out' }) =>
      api<void>(`/shifts/${shiftId}/checkin?kind=${kind}`, { method: 'DELETE' }),
    ...common,
  });
  return { set, clear };
}

export function useHandoverTick() {
  const invalidate = useInvalidateTimeline();
  return useMutation({
    mutationFn: (input: {
      kind: 'predaja' | 'nadaljevanje' | 'prevzem' | 'zaključek';
      fromShiftId: string;
      toShiftId: string;
      done: boolean;
    }) => api<unknown>('/handovers/tick', { method: 'POST', body: JSON.stringify(input) }),
    onError: mutationErrorHandler,
    onSettled: invalidate,
  });
}

export function useProblems() {
  return useQuery({ queryKey: ['problems'], queryFn: () => api<ProblemRow[]>('/problems') });
}

export function useResolveProblem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, resolved }: { id: string; resolved: boolean }) =>
      api<ProblemRow>(`/problems/${id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ resolved }),
      }),
    onError: mutationErrorHandler,
    onSettled: () => void qc.invalidateQueries({ queryKey: ['problems'] }),
  });
}

export function useMarkChangelogSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<unknown>('/changelog/seen', { method: 'POST' }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['changelog'] }),
  });
}
